import React, { useState } from "react";
import NextButton from "../NextButton";
import "../MobileWizard.scss";
import {
  IconButton,
  InputAdornment,
  OutlinedInput,
  Typography,
} from "@mui/material";
import { UserInfo } from "../MobileWizard";
import {
  CheckCircle,
  RadioButtonUnchecked,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

interface PasswordStepProps {
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  setUserInfo: React.Dispatch<React.SetStateAction<UserInfo>>;
  userInfo: UserInfo;
  isDesktop?: boolean;
}

const PasswordStep: React.FC<PasswordStepProps> = ({
  setActiveStep,
  userInfo,
  setUserInfo,
  isDesktop,
}) => {
  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    setUserInfo((prev) => ({ ...prev, password: event.target.value }));
  }
  function handleConfirmChange(event: React.ChangeEvent<HTMLInputElement>) {
    setUserInfo((prev) => ({ ...prev, confirmPassword: event.target.value }));
  }
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () =>
    setShowPassword((show: boolean) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const rules = getPasswordRules(userInfo.password);
  const allRulesMet = rules.every((rule) => rule.met);
  const passwordsMatch =
    userInfo.confirmPassword.length > 0 &&
    userInfo.password === userInfo.confirmPassword;
  const showMismatch = userInfo.confirmPassword.length > 0 && !passwordsMatch;

  return (
    <div className="stepBody">
      <div className="wizardStep__fieldWrap">
        <Typography variant="h4" className="wizardStep__heading">
          {" "}
          Create a password
        </Typography>

        <ul className="passwordStep__checklist">
          {rules.map((rule) => (
            <li
              key={rule.label}
              className={`passwordStep__rule${
                rule.met ? " passwordStep__rule--met" : ""
              }`}
            >
              {rule.met ? (
                <CheckCircle className="passwordStep__ruleIcon" />
              ) : (
                <RadioButtonUnchecked className="passwordStep__ruleIcon" />
              )}
              {rule.label}
            </li>
          ))}
        </ul>

        <Typography className="wizardStep__label"> Password </Typography>
        <OutlinedInput
          style={{ width: isDesktop ? "50%" : "100%" }}
          id="outlined-adornment-password"
          type={showPassword ? "text" : "password"}
          value={userInfo.password}
          onChange={handleChange}
          autoComplete="new-password"
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
          className="customedInput"
          name="password"
        />

        <Typography className="wizardStep__label"> Confirm password </Typography>
        <OutlinedInput
          style={{ width: isDesktop ? "50%" : "100%" }}
          type={showPassword ? "text" : "password"}
          value={userInfo.confirmPassword}
          onChange={handleConfirmChange}
          autoComplete="new-password"
          error={showMismatch}
          className="customedInput"
          name="confirmPassword"
        />
        {showMismatch && (
          <Typography className="passwordStep__error">
            Passwords don't match
          </Typography>
        )}
      </div>

      <NextButton
        label="Next"
        setActiveStep={setActiveStep}
        disabled={!allRulesMet || !passwordsMatch}
      ></NextButton>
    </div>
  );
};

export default PasswordStep;

interface PasswordRule {
  label: string;
  met: boolean;
}

const getPasswordRules = (password: string): PasswordRule[] => [
  { label: "One uppercase letter (A-Z)", met: /[A-Z]/.test(password) },
  {
    label: "One special character (!#@)",
    met: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  },
  { label: "One number (0-9)", met: /[0-9]/.test(password) },
  { label: "At least 8 characters", met: password.length >= 8 },
];
